// src/Main.tsx
import React, { useState } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  Audio,
  staticFile,
  interpolate,
  spring,
  Series,
} from 'remotion';

const resolveSrc = (src: string) => {
  if (!src) return '';
  return src.startsWith('http://') || src.startsWith('https://')
    ? src
    : staticFile(src);
};

// Remotion の <Img> の代わりに、読み込みエラーで落ちない SafeImg を使用
const SafeImg: React.FC<{
  src: string;
  style?: React.CSSProperties;
}> = ({ src, style }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div
        style={{
          ...style,
          backgroundColor: '#222',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '3vmin',
          fontWeight: 'bold',
        }}
      >
        NO IMAGE
      </div>
    );
  }

  return (
    <img
      src={src}
      style={style}
      onError={() => {
        console.warn(`画像のロード失敗をスキップしました: ${src}`);
        setHasError(true);
      }}
      alt=""
    />
  );
};

const ProductScene: React.FC<{
  rank: string;
  productName: string;
  price: string;
  description: string;
  imageSrc: string;
  audioSrc: string;
}> = ({ rank, productName, price, description, imageSrc, audioSrc }) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const cardSpring = spring({
    frame,
    fps,
    config: { stiffness: 60 },
  });

  const bgScale = interpolate(frame, [0, durationInFrames], [1, 1.2], {
    extrapolateRight: 'clamp',
  });

  // 商品名の長さに応じたフォントサイズの適正化
  const getProductNameFontSize = (text: string) => {
    if (text.length > 25) return '3.8vmin';
    if (text.length > 15) return '4.5vmin';
    return '5.2vmin';
  };

  const resolvedImageSrc = resolveSrc(imageSrc);

  return (
    <AbsoluteFill style={{ backgroundColor: 'black', overflow: 'hidden' }}>
      {/* 背景ブラー */}
      <AbsoluteFill
        style={{
          transform: `scale(${bgScale})`,
          filter: 'blur(59px)',
          opacity: 0.6,
        }}
      >
        {resolvedImageSrc ? (
          <SafeImg
            src={resolvedImageSrc}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : null}
      </AbsoluteFill>

      {/* メインカード */}
      <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div
          style={{
            width: '90%',
            height: '92%',
            backgroundColor: 'white',
            borderRadius: '3.4vmin',
            position: 'relative',
            transform: `scale(${cardSpring})`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '3.5vmin 4vmin',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          {/* 左上おすすめラベル */}
          <div
            style={{
              position: 'absolute',
              top: '3.5%',
              left: '5%',
              fontSize: '2.8vmin',
              fontWeight: '800',
              color: '#333',
              lineHeight: 1.2,
              letterSpacing: '0.05em',
              zIndex: 10,
            }}
          >
            じゃんぱら<br />個人的おすすめ
          </div>

          {/* 順位バッジ（右上） */}
          <div
            style={{
              position: 'absolute',
              top: '3%',
              right: '5%',
              fontSize: '6.5vmin',
              fontWeight: '900',
              color: 'white',
              WebkitTextStroke: '0.6vmin black',
              textShadow: '3px 3px 0px rgba(0,0,0,0.2)',
              zIndex: 10,
            }}
          >
            {rank}
          </div>

          {/* 1. 画像エリア（高さを35%に抑え、余白を調整） */}
          {resolvedImageSrc ? (
            <SafeImg
              src={resolvedImageSrc}
              style={{
                width: '100%',
                height: '35%',
                borderRadius: '2vmin',
                objectFit: 'contain',
                flexShrink: 0,
                marginTop: '5vmin',
              }}
            />
          ) : (
            <div style={{ height: '35%', flexShrink: 0 }} />
          )}

          {/* 2. 商品名（フォントサイズ・行高調整） */}
          <div
            style={{
              fontSize: getProductNameFontSize(productName),
              fontWeight: '800',
              color: 'black',
              lineHeight: 1.3,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flexShrink: 0,
              margin: '1vmin 0',
            }}
          >
            {productName}
          </div>

          {/* 3. 価格（8vmin にサイズ変更） */}
          <div
            style={{
              fontSize: '8vmin',
              fontWeight: '900',
              color: '#ffeaa7',
              WebkitTextStroke: '1.2vmin black',
              textAlign: 'center',
              paintOrder: 'stroke fill',
              flexShrink: 0,
              margin: '0.5vmin 0',
              letterSpacing: '0.02em',
            }}
          >
            {price}
          </div>

          {/* 4. コメント / 商品説明（3.2vmin に変更） */}
          <div
            style={{
              fontSize: '3.2vmin',
              fontWeight: '700',
              color: '#222',
              borderTop: '2px solid #ddd',
              paddingTop: '1.5vmin',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.3,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {description}
          </div>
        </div>
      </AbsoluteFill>

      {/* 音声 */}
      {typeof audioSrc === 'string' && audioSrc.length > 0 && (
        <Audio src={resolveSrc(audioSrc)} />
      )}
    </AbsoluteFill>
  );
};

export const Main: React.FC<{
  items: Array<{
    rank: string;
    productName: string;
    price: string;
    description: string;
    imageSrc: string;
    audioSrc: string;
    durationInFrames: number;
  }>;
}> = ({ items }) => {
  return (
    <AbsoluteFill>
      <Series>
        {items.map((item, index) => (
          <Series.Sequence
            key={`${item.rank}-${index}`}
            durationInFrames={item.durationInFrames}
          >
            <ProductScene {...item} />
          </Series.Sequence>
        ))}
      </Series>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
